/**
 * 平台级市场行情 WebSocket（/websocket/market）
 * 登录后连接，由页面通过 setMarketScope 声明 types / codes，重连后自动重发
 * 详情页可在同一连接上额外订阅当前产品周期性 K 线
 */
import wsManager from './websocket-manager'
import { VUE_APP_WS_URL } from '../config.js'
import { getRealtimePrices } from '@/api/market'
import { getToken } from '@/utils/auth'

const SUBSCRIBER_ID = 'app-global-market-ws'
const STALE_THRESHOLD_MS = 30000
const BACKGROUND_RECONNECT_MS = 3000
const STALE_CHECK_INTERVAL_MS = 15000
const LONG_BACKGROUND_MS = 30 * 60 * 1000
const MAX_RECONNECT_FAILURES = 3
const RECONNECT_DELAY_MS = 3000

let globalSubscription = null
let reconnectTimer = null
let staleCheckTimer = null
let pingTimer = null
let visibilitySetup = false
let lastMessageTime = 0
let backgroundSince = null
let reconnectFailCount = 0
let reconnectInProgress = false
let closingIntentionally = false
let initInProgress = null
let vuexContext = null
let currentKlineSymbol = null
let lastWsUrl = ''
let currentScope = { types: [], codes: [] }
let lastSentScopeKey = ''
const klineListeners = new Set()
const PING_INTERVAL_MS = 20000

export const MARKET_TYPE_CRYPTO = 2

function normalizeScope(scope) {
  const types = []
  const codes = []
  const rawTypes = (scope && scope.types) || []
  const rawCodes = (scope && scope.codes) || []
  rawTypes.forEach((item) => {
    const type = Number(item)
    if (Number.isInteger(type) && type >= 1 && type <= 5 && types.indexOf(type) === -1) {
      types.push(type)
    }
  })
  rawCodes.forEach((item) => {
    const code = String(item || '').trim()
    if (code && codes.indexOf(code) === -1) {
      codes.push(code)
    }
  })
  types.sort((a, b) => a - b)
  codes.sort()
  return { types, codes }
}

function scopeKey(scope) {
  return JSON.stringify(scope)
}

export function setMarketScope(scope) {
  const next = normalizeScope(scope || {})
  const changed = scopeKey(next) !== scopeKey(currentScope)
  currentScope = next
  if (changed && vuexContext && vuexContext.commit) {
    vuexContext.commit('RESET_MARKET_TICKS')
  }
  sendMarketScope(false)
  return currentScope
}

export function clearMarketScope() {
  return setMarketScope({ types: [], codes: [] })
}

function sendMarketScope(force) {
  const key = scopeKey(currentScope)
  if (!force && key === lastSentScopeKey && isMarketWsOpen()) {
    return true
  }
  const wsUrl = lastWsUrl || getMarketWsUrl()
  if (!wsUrl || !hasMarketWsAuth()) {
    return false
  }
  const ok = wsManager.send(wsUrl, JSON.stringify({
    action: 'set',
    types: currentScope.types,
    codes: currentScope.codes
  }))
  if (ok) {
    lastSentScopeKey = key
  }
  return ok
}

function resubscribeScope() {
  lastSentScopeKey = ''
  sendMarketScope(true)
}

export function hasMarketWsAuth() {
  return !!(localStorage.getItem('token') && localStorage.getItem('isLogin') === 'true')
}

export function getMarketWsUrl() {
  const token = localStorage.getItem('token')
  if (!token) {
    return ''
  }
  return `${VUE_APP_WS_URL}/market/${token}`
}

export function isMarketWsOpen() {
  const wsUrl = lastWsUrl || getMarketWsUrl()
  if (!wsUrl) {
    return false
  }
  const status = wsManager.getConnectionStatus(wsUrl)
  return !!(status.connected && status.readyState === WebSocket.OPEN)
}

export function isMarketWsFresh(maxAge = 8000) {
  if (lastMessageTime <= 0) {
    return false
  }
  return Date.now() - lastMessageTime <= maxAge
}

export function addKlineListener(fn) {
  if (typeof fn !== 'function') {
    return function () {}
  }
  klineListeners.add(fn)
  return function () {
    klineListeners.delete(fn)
  }
}

export function subscribeMarketKline(symbol) {
  currentKlineSymbol = symbol || null
  if (sendKlineAction(symbol, 'sub')) {
    return true
  }
  if (initInProgress) {
    initInProgress.then(() => {
      if (currentKlineSymbol && String(currentKlineSymbol).toLowerCase() === String(symbol).toLowerCase()) {
        sendKlineAction(symbol, 'sub')
      }
    }).catch(() => {})
  }
  return false
}

export function unsubscribeMarketKline(symbol) {
  const target = symbol || currentKlineSymbol
  if (currentKlineSymbol && target && String(currentKlineSymbol).toLowerCase() === String(target).toLowerCase()) {
    currentKlineSymbol = null
  }
  sendKlineAction(target, 'unsub')
}

function sendKlineAction(symbol, action) {
  const wsUrl = lastWsUrl || getMarketWsUrl()
  if (!symbol || !wsUrl || !hasMarketWsAuth()) {
    return false
  }
  return wsManager.send(wsUrl, JSON.stringify({
    type: 'KLINE',
    symbol: symbol,
    action: action
  }))
}

function resubscribeKline() {
  if (currentKlineSymbol) {
    sendKlineAction(currentKlineSymbol, 'sub')
  }
}

function handleMarketMessage(commit, data) {
  if (typeof data !== 'string') {
    return
  }
  const trimmed = data.trim()
  if (trimmed.startsWith('{')) {
    try {
      const obj = JSON.parse(trimmed)
      lastMessageTime = Date.now()
      if (obj && obj.type === 'KLINE' && obj.data) {
        klineListeners.forEach((fn) => {
          try {
            fn(obj)
          } catch (e) {
            // ignore listener errors
          }
        })
      }
    } catch (e) {
      // ignore non-json payloads (e.g. connection ack)
    }
    return
  }
  if (!trimmed.includes('[')) {
    return
  }
  try {
    const marketData = JSON.parse(trimmed)
    if (Array.isArray(marketData) && marketData.length > 0) {
      lastMessageTime = Date.now()
      commit('UPDATE_MARKET_TICKS', marketData)
    }
  } catch (e) {
    // ignore non-json payloads (e.g. connection ack)
  }
}

function handleConnectionStatus(commit, status) {
  commit('SET_MARKET_WS_CONNECTED', status === 'connected')
  if (status === 'connected') {
    reconnectFailCount = 0
    reconnectInProgress = false
    lastMessageTime = Date.now()
    startPing()
    resubscribeScope()
    resubscribeKline()
    return
  }
  if (status === 'disconnected' || status === 'error') {
    stopPing()
    globalSubscription = null
    if (!closingIntentionally) {
      scheduleReconnect(commit)
    }
  }
}

function scheduleReconnect(commit) {
  if (reconnectTimer || document.hidden || !hasMarketWsAuth()) {
    return
  }
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    if (!document.hidden && vuexContext && hasMarketWsAuth()) {
      forceReconnectMarketWs(vuexContext).catch(() => {})
    }
  }, RECONNECT_DELAY_MS)
}

function isConnectionStale() {
  if (lastMessageTime <= 0) {
    return false
  }
  return Date.now() - lastMessageTime > STALE_THRESHOLD_MS
}

function needsReconnect(bgDuration = 0) {
  if (!hasMarketWsAuth()) {
    return false
  }
  if (reconnectInProgress || closingIntentionally || initInProgress) {
    return false
  }
  if (isMarketWsOpen() && (lastMessageTime <= 0 || isMarketWsFresh(STALE_THRESHOLD_MS))) {
    return false
  }
  const wsUrl = lastWsUrl || getMarketWsUrl()
  if (!wsUrl) {
    return true
  }
  const status = wsManager.getConnectionStatus(wsUrl)
  return (
    bgDuration >= BACKGROUND_RECONNECT_MS ||
    !status.connected ||
    status.readyState !== WebSocket.OPEN ||
    isConnectionStale()
  )
}

async function fetchPricesFallback({ commit, state }) {
  if (!getToken()) {
    return
  }

  const tickMap = state.marketTickMap || {}
  const codes = Object.keys(tickMap)
  if (!codes.length) {
    return
  }

  try {
    const res = await getRealtimePrices(codes)
    if ((res.code === 200 || res.code === '200') && res.data) {
      const ticks = Object.entries(res.data).map(([code, price]) => {
        const existing = tickMap[code] || {}
        return {
          ...existing,
          code,
          price: Number(price),
        }
      })
      if (ticks.length) {
        commit('UPDATE_MARKET_TICKS', ticks)
      }
    }
  } catch (e) {
    // HTTP 补价失败不影响 WS 重连
  }
}

function startPing() {
  stopPing()
  pingTimer = setInterval(() => {
    const wsUrl = lastWsUrl || getMarketWsUrl()
    if (!wsUrl || !isMarketWsOpen()) {
      return
    }
    wsManager.send(wsUrl, JSON.stringify({ type: 'PING' }))
  }, PING_INTERVAL_MS)
}

function stopPing() {
  if (pingTimer) {
    clearInterval(pingTimer)
    pingTimer = null
  }
}

function closeCurrentConnection(commit) {
  closingIntentionally = true
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  stopStaleCheck()
  stopPing()
  const wsUrl = lastWsUrl || getMarketWsUrl()
  if (wsUrl) {
    wsManager.forceCloseConnection(wsUrl)
  }
  lastWsUrl = ''
  lastSentScopeKey = ''
  globalSubscription = null
  lastMessageTime = 0
  if (commit) {
    commit('SET_MARKET_WS_CONNECTED', false)
  }
  closingIntentionally = false
}

export function closeMarketWs(context) {
  vuexContext = context || vuexContext
  const commit = context && context.commit
  closeCurrentConnection(commit)
}

export async function forceReconnectMarketWs(context) {
  if (reconnectInProgress) {
    return globalSubscription
  }
  reconnectInProgress = true

  if (initInProgress) {
    try {
      await initInProgress
    } catch (e) {
      // ignore a failed in-flight init before replacing the socket
    }
  }

  const commit = context && context.commit
  closeCurrentConnection(commit)

  if (!hasMarketWsAuth()) {
    reconnectInProgress = false
    return null
  }

  try {
    const subscription = await initMarketWs(context, true)
    reconnectFailCount = 0
    return subscription
  } catch (error) {
    reconnectFailCount += 1
    throw error
  } finally {
    reconnectInProgress = false
  }
}

async function reconnectWithFallback(context, bgDuration = 0) {
  await fetchPricesFallback(context)

  try {
    await forceReconnectMarketWs(context)
  } catch (error) {
    if (
      reconnectFailCount >= MAX_RECONNECT_FAILURES &&
      bgDuration >= LONG_BACKGROUND_MS
    ) {
      window.location.reload()
      return
    }
    scheduleReconnect(context.commit)
  }
}

async function handleAppForeground() {
  if (!vuexContext || document.hidden || !hasMarketWsAuth()) {
    return
  }

  const bgDuration = backgroundSince ? Date.now() - backgroundSince : 0
  backgroundSince = null
  startStaleCheck()

  if (needsReconnect(bgDuration)) {
    await reconnectWithFallback(vuexContext, bgDuration)
  }
}

function handleAppBackground() {
  backgroundSince = Date.now()
  stopStaleCheck()
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
}

function startStaleCheck() {
  stopStaleCheck()
  staleCheckTimer = setInterval(() => {
    if (document.hidden || !vuexContext || !hasMarketWsAuth()) {
      return
    }
    if (needsReconnect(0)) {
      reconnectWithFallback(vuexContext, 0)
    }
  }, STALE_CHECK_INTERVAL_MS)
}

function stopStaleCheck() {
  if (staleCheckTimer) {
    clearInterval(staleCheckTimer)
    staleCheckTimer = null
  }
}

function setupVisibilityListeners() {
  if (visibilitySetup) {
    return
  }
  visibilitySetup = true

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      handleAppBackground()
    } else {
      handleAppForeground()
    }
  })

  window.addEventListener('focus', () => {
    handleAppForeground()
  })

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      handleAppForeground()
    }
  })
}

/**
 * 初始化平台级市场 WS（幂等；force=true 时强制新建连接）
 */
export async function initMarketWs(context, force = false) {
  vuexContext = context
  const { commit } = context

  if (!hasMarketWsAuth()) {
    closeCurrentConnection(commit)
    return null
  }

  if (globalSubscription && !force) {
    setupVisibilityListeners()
    startStaleCheck()
    startPing()
    return globalSubscription
  }

  if (initInProgress && !force) {
    return initInProgress
  }

  const wsUrl = getMarketWsUrl()
  if (!wsUrl) {
    return null
  }

  const run = (async () => {
    lastWsUrl = wsUrl
    globalSubscription = await wsManager.subscribe(
      wsUrl,
      SUBSCRIBER_ID,
      (data) => handleMarketMessage(commit, data),
      (status) => handleConnectionStatus(commit, status)
    )

    setupVisibilityListeners()
    startStaleCheck()
    startPing()
    if (isMarketWsOpen()) {
      commit('SET_MARKET_WS_CONNECTED', true)
      lastMessageTime = Date.now()
      resubscribeScope()
      resubscribeKline()
    }
    return globalSubscription
  })()

  initInProgress = run
  try {
    return await run
  } finally {
    if (initInProgress === run) {
      initInProgress = null
    }
  }
}
